import { ProductType } from 'domain/Product';
import { Nullable } from 'utils/types';

export enum FIELDS {
  APPLICANT_1 = 'applicant1',
  APPLICANT_2 = 'applicant2',
  APPLICATION_DATE = 'applicationDate',
  APPLICANT_SURNAME = 'applicantSurname',
  APPLICATION_ID = 'applicationId',
  CREDIT_POLICY_EXCEPTION_RATIONALE = 'creditPolicyExemptionRationale',
  DELEGATED_AUTHORITY_LEVEL = 'delegatedAuthorityLevel',
  FIRM_NAME = 'firmName',
  IS_CREDIT_POLICY_EXEMPTION = 'isCreditPolicyExemption',
  NET_LOAN = 'netLoan',
  UNDERWRITER = 'underwriter',
  UNDERWRITER_RECOMMENDATION = 'underwriterRecommendation',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  GENDER = 'gender',
  AGE = 'age',
  NATIONALITY = 'nationality',
  MARITAL_STATUS = 'maritalStatus',
  POWER_OF_ATTORNEY_FULL_NAME = 'powerOfAttorneyFullName',
  EMPLOYMENT_STATUS = 'employmentStatus',
  PLANNED_RETIREMENT_AGE = 'plannedRetirementAge',
  PROOF_OF_ID = 'proofOfId',
  PROOF_OF_ADDRESS = 'proofOfAddress',

  CCJ_AMOUNT = 'ccjAmount',
  CCJ_LATEST_DATE = 'ccjLatestDate',
  DEFAULT_AMOUNT = 'defaultAmount',
  DEFAULT_LATEST_DATE = 'defaultLatestDate',
  MORTGAGE_ARREARS_AMOUNT = 'mortgageArrearsAmount',
  MORTGAGE_ARREARS_LATEST_DATE = 'mortgageArrearsLatestDate',
  BANKRUPTCY_AMOUNT = 'bankruptcyAmount',
  BANKRUPTCY_LATEST_DATE = 'bankruptcyLatestDate',
  HAS_CRIMINAL_CONVICTIONS = 'hasCriminalConvictions',
  CRIMINAL_CONVICTIONS_DETAILS = 'criminalConvictionsDetails',
  HAS_ADVERSE_CREDIT = 'hasAdverseCredit',
  ADVERSE_CREDIT_DETAILS = 'adverseCreditDetails',
  DELPHI_SCORE = 'delphiScore',

  PRODUCT_NAME = 'productName',
  PRODUCT_TERM_LENGTH = 'productTermLength',
  PRODUCT_TYPE = 'productType',
  FIXED_INTEREST_RATE = 'fixedInterestRate',
  LOAN_PURPOSE = 'loanPurpose',
  LOAN_PURCHASE = 'loanPurchase',
  LOAN_REMORTGAGE = 'loanRemortgage',
  DEBT_CONSOLIDATION = 'debtConsolidation',
  HOME_IMPROVEMENTS = 'homeImprovements',
  DISCRETIONARY_SPEND = 'discretionarySpend',
  ASSISTING_OTHERS = 'assistingOthers',
  OTHER_PURPOSES = 'otherPurposes',
  GROSS_LOAN = 'grossLoan',
  LOAN_TO_VALUE = 'loanToValue',
  DEBT_TO_INCOME = 'debtToIncome',

  FLAT_NUMBER = 'flatNumber',
  BUILDING_NUMBER = 'buildingNumber',
  BUILDING_NAME = 'buildingName',
  STREET = 'street',
  CITY = 'city',
  REGION = 'region',
  POSTCODE = 'postcode',
  TYPE = 'type',
  TENURE = 'tenure',
  REMAINING_LEASE_YEARS = 'remainingLeaseYears',
  VALUATION_TYPE = 'valuationType',
  VALUATION_INSPECTION_DATE = 'valuationInspectionDate',
  VALUATION_ESTIMATED_VALUE = 'valuationEstimatedValue',
  VALUATION_CONFIRMED_VALUE = 'valuationConfirmedValue',

  CURRENT_INCOME = 'current',
  FUTURE_INCOME = 'future',
  SURVIVOR = 'survivor',
  SURVIVOR_INCOME = 'survivorIncome',

  EMPLOYMENT_INCOME = 'employmentIncome',
  SELF_EMPLOYMENT_INCOME = 'selfEmploymentIncome',
  OTHER_INCOME = 'otherIncome',
  PENSION_INCOME = 'pensionIncome',
  TOTAL_INCOME = 'totalIncome',
  COMBINED_INCOME = 'combined',

  ASSET_TYPE = 'type',
  ASSET_VALUE = 'value',
  ASSET_DESCRIPTION = 'description',

  COMMITMENT_MONTHLY = 'monthly',
  COMMITMENT_BALANCE = 'balance',
  COMMITMENT_COMBINED_MONTHLY_BASIC_SPEND = 'combinedMonthlyBasicSpend',
  COMMITMENT_TO_BE_CLEARED = 'toBeCleared',
  COMMITMENT_REMAINING = 'remaining',
  COMMITMENT_TOTAL = 'total',

  MAXIMUM_LOAN_POSSIBLE = 'maximumLoanPossible',
  DISPOSABLE_INCOME = 'disposableIncome',
  DISPOSABLE_INCOME_BUFFER = 'disposableIncomeBuffer',
  STAGE = 'stage',
  LIVING_COSTS = 'livingCosts',
  NET_CASHFLOW = 'netCashflow',

  SPECIAL_CONDITIONS = 'specialConditions',

  INITIAL_ASSESSMENT_BY = 'initialAssessmentBy',
  INITIAL_ASSESSMENT_DATE = 'initialAssessmentDate',
  APPROVED_BY = 'approvedBy',
  APPROVED_DATE = 'approvedDate',
}

export enum RepaymentStrategyField {
  HaveSaleOfPropertyAndDownsizing = 'haveSaleOfPropertyAndDownsizing',
  HaveSaleOfAnotherProperty = 'haveSaleOfAnotherProperty',
  HaveInvestments = 'haveInvestments',
  HaveEndownments = 'haveEndownments',
  HaveOther = 'haveOther',
}

export enum SUBDOMAINS {
  SUMMARY = 'summary',
  LOAN = 'loan',
  BORROWER = 'borrower',
  CREDIT_RECORD = 'creditRecord',
  MORTGAGE_PROPERTY = 'mortgageProperty',
  INCOME = 'income',
  ASSETS = 'assets',
  EXPENDITURE = 'expenditure',
  AFFORDABILITY = 'affordability',
  SPECIAL_CONDITIONS = 'specialConditions',
  APPROVALS = 'approvals',
  REPAYMENT_STRATEGY = 'repaymentStrategy',
}

export type ApplicantRelated<T> = {
  [FIELDS.APPLICANT_1]: T;
  [FIELDS.APPLICANT_2]: Nullable<T>;
};

export type Summary = {
  [FIELDS.APPLICATION_ID]: Nullable<string>;
  [FIELDS.APPLICATION_DATE]: Nullable<string>;
  [FIELDS.APPLICANT_SURNAME]: Nullable<string>;
  [FIELDS.UNDERWRITER]: Nullable<string>;
  [FIELDS.FIRM_NAME]: Nullable<string>;
  [FIELDS.DELEGATED_AUTHORITY_LEVEL]: Nullable<string>;
  [FIELDS.HAS_ADVERSE_CREDIT]: Nullable<boolean>;
  [FIELDS.IS_CREDIT_POLICY_EXEMPTION]: Nullable<boolean>;
  [FIELDS.CREDIT_POLICY_EXCEPTION_RATIONALE]: Nullable<string>;
  [FIELDS.UNDERWRITER_RECOMMENDATION]: Nullable<string>;
};

export type Borrower = ApplicantRelated<{
  [FIELDS.FIRST_NAME]: Nullable<string>;
  [FIELDS.LAST_NAME]: Nullable<string>;
  [FIELDS.GENDER]: Nullable<string>;
  [FIELDS.AGE]: Nullable<number>;
  [FIELDS.NATIONALITY]: Nullable<string>;
  [FIELDS.MARITAL_STATUS]: Nullable<string>;
  [FIELDS.POWER_OF_ATTORNEY_FULL_NAME]: Nullable<string>;
  [FIELDS.EMPLOYMENT_STATUS]: Nullable<string>;
  [FIELDS.PLANNED_RETIREMENT_AGE]: Nullable<number>;
  [FIELDS.PROOF_OF_ID]: Nullable<string>;
  [FIELDS.PROOF_OF_ADDRESS]: Nullable<string>;
}>;

export type CreditRecord = ApplicantRelated<{
  [FIELDS.CCJ_AMOUNT]: Nullable<unknown>;
  [FIELDS.CCJ_LATEST_DATE]: Nullable<string>;
  [FIELDS.DEFAULT_AMOUNT]: Nullable<string>;
  [FIELDS.DEFAULT_LATEST_DATE]: Nullable<string>;
  [FIELDS.MORTGAGE_ARREARS_AMOUNT]: Nullable<number>;
  [FIELDS.MORTGAGE_ARREARS_LATEST_DATE]: Nullable<string>;
  [FIELDS.BANKRUPTCY_AMOUNT]: Nullable<number>;
  [FIELDS.BANKRUPTCY_LATEST_DATE]: Nullable<string>;
  [FIELDS.HAS_CRIMINAL_CONVICTIONS]: Nullable<boolean>;
  [FIELDS.CRIMINAL_CONVICTIONS_DETAILS]: Nullable<string>;
  [FIELDS.HAS_ADVERSE_CREDIT]: Nullable<boolean>;
  [FIELDS.ADVERSE_CREDIT_DETAILS]: Nullable<string>;
}> & {
  [FIELDS.DELPHI_SCORE]: Nullable<number>;
};

export type Loan = {
  [FIELDS.PRODUCT_NAME]: Nullable<string>;
  [FIELDS.FIXED_INTEREST_RATE]: Nullable<number>;
  [FIELDS.LOAN_PURPOSE]: Nullable<string>;
  [FIELDS.LOAN_PURCHASE]: Nullable<number>;
  [FIELDS.LOAN_REMORTGAGE]: Nullable<number>;
  [FIELDS.DEBT_CONSOLIDATION]: Nullable<number>;
  [FIELDS.HOME_IMPROVEMENTS]: Nullable<number>;
  [FIELDS.DISCRETIONARY_SPEND]: Nullable<number>;
  [FIELDS.ASSISTING_OTHERS]: Nullable<number>;
  [FIELDS.OTHER_PURPOSES]: Nullable<number>;
  [FIELDS.GROSS_LOAN]: Nullable<number>;
  [FIELDS.NET_LOAN]: Nullable<number>;
  [FIELDS.LOAN_TO_VALUE]: Nullable<number>;
  [FIELDS.DEBT_TO_INCOME]: Nullable<number>;
  [FIELDS.PRODUCT_TERM_LENGTH]: Nullable<number>;
  [FIELDS.PRODUCT_TYPE]: ProductType;
};

export type MortgageProperty = {
  [FIELDS.FLAT_NUMBER]: Nullable<string>;
  [FIELDS.BUILDING_NUMBER]: Nullable<string>;
  [FIELDS.BUILDING_NAME]: Nullable<string>;
  [FIELDS.STREET]: Nullable<string>;
  [FIELDS.CITY]: Nullable<string>;
  [FIELDS.REGION]: Nullable<string>;
  [FIELDS.POSTCODE]: Nullable<string>;
  [FIELDS.TYPE]: Nullable<string>;
  [FIELDS.TENURE]: Nullable<string>;
  [FIELDS.REMAINING_LEASE_YEARS]: Nullable<number>;
  [FIELDS.VALUATION_TYPE]: Nullable<string>;
  [FIELDS.VALUATION_INSPECTION_DATE]: Nullable<string>;
  [FIELDS.VALUATION_ESTIMATED_VALUE]: Nullable<number>;
  [FIELDS.VALUATION_CONFIRMED_VALUE]: Nullable<number>;
};

export type ApplicantIncome = {
  [FIELDS.EMPLOYMENT_INCOME]: Nullable<number>;
  [FIELDS.SELF_EMPLOYMENT_INCOME]: Nullable<number>;
  [FIELDS.OTHER_INCOME]: Nullable<number>;
  [FIELDS.PENSION_INCOME]: Nullable<number>;
  [FIELDS.TOTAL_INCOME]: Nullable<number>;
};

export type ApplicantSurvivorIncome = {
  [FIELDS.SURVIVOR_INCOME]: Nullable<number>;
};

export type Income = {
  [FIELDS.CURRENT_INCOME]: ApplicantRelated<ApplicantIncome> & { [FIELDS.COMBINED_INCOME]: Nullable<number> };
  [FIELDS.FUTURE_INCOME]: ApplicantRelated<ApplicantIncome> & { [FIELDS.COMBINED_INCOME]: Nullable<number> };
  [FIELDS.SURVIVOR]: ApplicantRelated<ApplicantSurvivorIncome>;
};

type Asset = {
  [FIELDS.ASSET_TYPE]: Nullable<string>;
  [FIELDS.ASSET_VALUE]: Nullable<number>;
  [FIELDS.ASSET_DESCRIPTION]: Nullable<string>;
};

export type Assets = ApplicantRelated<Asset[]>;

export type Commitment = {
  [FIELDS.COMMITMENT_TO_BE_CLEARED]: Nullable<number>;
  [FIELDS.COMMITMENT_REMAINING]: Nullable<number>;
  [FIELDS.COMMITMENT_TOTAL]: Nullable<number>;
};

export type Expenditure = ApplicantRelated<{
  [FIELDS.COMMITMENT_MONTHLY]: Commitment;
  [FIELDS.COMMITMENT_BALANCE]: Commitment;
}> & { [FIELDS.COMMITMENT_COMBINED_MONTHLY_BASIC_SPEND]: Nullable<number> };

export type Affordability = {
  [FIELDS.MAXIMUM_LOAN_POSSIBLE]: Nullable<number>;
  [FIELDS.DISPOSABLE_INCOME]: Nullable<number>;
  [FIELDS.DISPOSABLE_INCOME_BUFFER]: Nullable<number>;
  [FIELDS.STAGE]: Nullable<string>;
  [FIELDS.LIVING_COSTS]: Nullable<number>;
  [FIELDS.NET_CASHFLOW]: Nullable<number>;
};
export type SpecialConditions = {
  [FIELDS.SPECIAL_CONDITIONS]: Nullable<string>;
};

export type Approvals = {
  [FIELDS.INITIAL_ASSESSMENT_BY]: Nullable<string>;
  [FIELDS.INITIAL_ASSESSMENT_DATE]: Nullable<string>;
  [FIELDS.APPROVED_BY]: Nullable<string>;
  [FIELDS.APPROVED_DATE]: Nullable<string>;
};

export interface RepaymentStrategy {
  [RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing]: Nullable<boolean>;
  [RepaymentStrategyField.HaveSaleOfAnotherProperty]: Nullable<boolean>;
  [RepaymentStrategyField.HaveInvestments]: Nullable<boolean>;
  [RepaymentStrategyField.HaveEndownments]: Nullable<boolean>;
  [RepaymentStrategyField.HaveOther]: Nullable<boolean>;
}

export type Underwriting = {
  [SUBDOMAINS.SUMMARY]: Summary;
  [SUBDOMAINS.LOAN]: Loan;
  [SUBDOMAINS.BORROWER]: Borrower;
  [SUBDOMAINS.CREDIT_RECORD]: CreditRecord;
  [SUBDOMAINS.MORTGAGE_PROPERTY]: MortgageProperty;
  [SUBDOMAINS.INCOME]: Income;
  [SUBDOMAINS.ASSETS]: Assets;
  [SUBDOMAINS.EXPENDITURE]: Expenditure;
  [SUBDOMAINS.AFFORDABILITY]: Affordability;
  [SUBDOMAINS.SPECIAL_CONDITIONS]: SpecialConditions;
  [SUBDOMAINS.APPROVALS]: Approvals;
  [SUBDOMAINS.REPAYMENT_STRATEGY]: RepaymentStrategy;
};
